/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG, GMTVP, NON_TVP_FCORGS, ScenarioSelectionSource} from '../../../common/constants';
import {
  TableDisplayPopUpComponent
} from '../../../core/components/pop-ups/table-display-pop-up/table-display-pop-up.component';
import {getDefaultYearRange, range} from '../../../core/helpers/generic-helpers';
import {StateService} from '../../../core/state/state.service';
import {PropulsionScenario} from '../../../models/propulsion-scenario-dto-model';
import {SelectableOptions} from '../../../models/selectable-options';
import {UpdateForecastWithScenarioRequest} from '../../../models/update-veh-forecast-with-scenario-request.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';
import {RegionService} from '../../../services/region.service';

@Component({
    selector: 'app-update-propulsion-scenario-with-scenario',
    templateUrl: './update-propulsion-scenario-with-scenario.component.html',
    styleUrl: './update-propulsion-scenario-with-scenario.component.scss',
    standalone: false
})
export class UpdatePropulsionScenarioWithScenarioComponent implements OnInit {
  protected readonly ScenarioSelectionSource = ScenarioSelectionSource;
  protected readonly GMTVP = GMTVP;

  SUBREGION = 'SUBREGION';
  PLNGMKT = 'PLNGMKT';
  MKTDIV = 'MKTDIV';
  BRANDFAM = 'BRANDFAM';
  BODYSTYLE = 'BODYSTYLE';
  PRGMCODE = 'PRGMCODE';

  sourceData = new Map<string, any>;

  showSpinner: boolean;
  selectedScenarioSource: ScenarioSelectionSource;

  allFilterOptions: any[];
  filteredData: any[];

  workingScenario: PropulsionScenario;
  sourceScenario: PropulsionScenario;

  validFcorgs: string[] = [];
  regions: string[];
  selectedRegion: string;
  subregions = new SelectableOptions();
  planningMarkets = new SelectableOptions();
  marketDivisions = new SelectableOptions();
  brandfams = new SelectableOptions();
  bodyStyles = new SelectableOptions();
  programCodes = new SelectableOptions();

  startYear: number;
  endYear: number;
  yearRange: number[];

  alwaysPresentColumns: string [] = ['PROP_SCEN_ID', 'PROP_SCEN_DTL_ID', 'MKT', 'PLNGMKT', 'MKTDIV', 'BRAND', 'LBRAND',
    'BRANDFAM', 'PRGMCODE', 'STYLECODE', 'BODYSTYLE', 'ENGFAM', 'ENGRPO', 'ENGDSP', 'ENGCYL', 'VFUEL', 'ENGCAM', 'ENGPWR',
    'ENGTORQ', 'INJECTION', 'BOOST', 'TRANFAM', 'TRANRPO', 'TRANNAME', 'TRANTYPE', 'TRANSPEED', 'CELLFAM', 'BATRPO',
    'BATDESC', 'ELECFAM', 'ELECRPO', 'ELECDESC', 'PRIDRVRPO', 'PRIDRVDESC', 'SECDRVRPO', 'SECDRVDESC', 'DRIVETYPE'
  ];


  constructor(readonly propulsionScenarioService: PropulsionScenarioService,
              readonly regionService: RegionService,
              readonly stateService: StateService,
              readonly excelService: ExcelExportService,
              public dialog: MatDialog,
              public snackBar: FabMessageService) {
  }

  ngOnInit() {
    this.showSpinner = false;
    this.workingScenario = this.stateService.selectedScenario.propulsionScenario;
    this.validFcorgs = this.workingScenario.forecastOrganization.toUpperCase() === GMTVP ? NON_TVP_FCORGS.filter(element => element !== 'GME') : [this.workingScenario.forecastOrganization]
    this.selectedScenarioSource = ScenarioSelectionSource.FCORG;
    this.getDefaultYearRange();
    this.getRegions();
  }

  /**
   * Gets the regions from the propulsion scenario ID
   */
  getRegions() {
    this.showSpinner = true;
    this.regionService.getAllRegionsByPropulsionScenario(this.stateService.getUserGmid(), this.workingScenario.id).subscribe({
      next: (data) => {
        const fetchedRegions = data.map((element: { region: any }) => element.region);
        if (this.workingScenario?.forecastOrganization === GMTVP) {
          this.regions = ['All'].concat(fetchedRegions);
        } else {
          this.regions = data.map((element: { region: any }) => element.region);
        }
        this.selectedRegion = this.regions[0];
      },
      error: (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.getFilters();
        this.resetAllFilterOptions();
        this.resetAllSelectedFilter();
      }
    });
  }

  /**
   * Gets the filters for the update
   */
  getFilters() {
    this.showSpinner = true;
    const gmid = this.stateService.getUserGmid();
    this.propulsionScenarioService.getUpdateForecastFilters(gmid, this.workingScenario.id, this.selectedRegion).subscribe(
      {
        next: (data) => {
          this.allFilterOptions = data;
          this.filteredData = data;
        },
        error: (error) => {
          this.showSpinner = false;
          this.snackBar.displayError(error, DISMISS_MSG);
        },
        complete: () => {
          this.initializeFilterOptions();
          this.showSpinner = false;
        }
      }
    );
  }

  /**
   * gets the filtered records that the user can view
   */
  async getFilteredRecords() {
    const workingDataMap = new Map<string, any>;
    const request = this.generateRequest(0, "");
    const columnsToDisplay = this.getTableColumns();

    this.showSpinner = true;
    try {
      const filteredRecords = await this.propulsionScenarioService.getFilteredRecords(request).toPromise();
      workingDataMap.set(this.workingScenario.id.toString(), filteredRecords);
      this.showSpinner = false;
      this.openRecordPopUp(workingDataMap, 'Working Scenario Filtered Records', columnsToDisplay);
    } catch (error) {
      this.showSpinner = false;
      this.snackBar.displayError(error, DISMISS_MSG);
    }
  }

  async getSourceRecords() {
    this.sourceData = new Map<string, any>();
    const request = this.generateRequest(this.sourceScenario.id, this.sourceScenario.forecastOrganization);
    const columnsToDisplay = this.getTableColumns();

    this.showSpinner = true;
    try {
      const sourceRecords = await this.propulsionScenarioService.getSourceRecords(request).toPromise();
      this.sourceData.set(this.sourceScenario.id.toString(), sourceRecords);
      this.showSpinner = false;
      this.openRecordPopUp(this.sourceData, 'Source Records', columnsToDisplay);
    } catch (error) {
      this.showSpinner = false;
      const message = error.message ? error.message : "Error fetching source records";
      this.snackBar.displayError(message, DISMISS_MSG);
    }
  }

  /**
   * Gets the table columns to display
   * @private
   */
  private getTableColumns(): string[] {
    const columnsToDisplay = [];
    for (const column of this.alwaysPresentColumns) {
      columnsToDisplay.push(column);
    }
    for (const year of range(this.startYear, this.endYear + 1)) {
      columnsToDisplay.push(year.toString());
    }
    return columnsToDisplay;
  }

  /**
   * Opens the pop up to view records
   * @param data the data to view
   * @param title the title of the pop up
   * @param displayedColumns the columns to display
   */
  openRecordPopUp(data: Map<string, any>, title: string, displayedColumns: string[]) {
    this.dialog.open(TableDisplayPopUpComponent, {
      height: '90%',
      width: '90%',
      data: {
        title: title,
        tableData: data,
        displayedColumns: displayedColumns,
      }
    });
  }

  /**
   * performs the update
   */
  async updateScenario() {
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    this.showSpinner = true;
    try {
      const request = this.generateRequest(this.sourceScenario.id, '');
      const fileName = 'UpdateWithOtherProp_' + this.workingScenario.id + ' _ ' + currDate;
      const response = await this.propulsionScenarioService.updateForecastWithScenario(request).toPromise();
      this.excelService.exportToExcelFromServer(response, fileName);
      this.snackBar.displaySuccess("Scenario successfully updated", DISMISS_MSG);
    } catch (error) {
      this.snackBar.displayError(error, DISMISS_MSG);
    } finally {
      this.showSpinner = false;
    }
  }

  /**
   * Gets the default year range for the reports
   */
  getDefaultYearRange(): void {
    const {yearRange} = getDefaultYearRange();
    const currentYear = new Date().getFullYear();
    this.yearRange = yearRange;
    this.startYear = currentYear;
    this.endYear = currentYear;
  }

  /**
   * Generates the request for getting records and updating
   * @param sourceScenarioId the id of the source scenario
   * @param fcorg the fcorg of the source scenario
   */
  generateRequest(sourceScenarioId: number, fcorg: string): UpdateForecastWithScenarioRequest {
    // fcorg is empty during update request and filtered records request
    if (fcorg == '') {
      switch (this.selectedScenarioSource) {
        //If fcorg, there is one source scenario, and we set the request fcorg to that scenarios fcorg
        case ScenarioSelectionSource.FCORG:
          fcorg = this.sourceScenario ? this.sourceScenario.forecastOrganization : '';
          break;
        // if tvp, then the request fcorg is GMTVP
        case ScenarioSelectionSource.TVP:
          fcorg = GMTVP;
          break;
      }
    }
    return {
      gmid: this.stateService.getUserGmid(),
      scenarioId: this.workingScenario.id,
      fcorg: fcorg,
      region: this.selectedRegion,
      subregion: this.subregions.selectedOptions.length > 0 ? this.subregions.selectedOptions[0] : 'ALL',
      planningMarket: this.planningMarkets.selectedOptions.length > 0 ? this.planningMarkets.selectedOptions[0] : 'ALL',
      marketDivision: this.marketDivisions.selectedOptions.length > 0 ? this.marketDivisions.selectedOptions[0] : 'ALL',
      brandFam: this.brandfams.selectedOptions.length > 0 ? this.brandfams.selectedOptions[0] : 'ALL',
      bodyStyle: this.bodyStyles.selectedOptions.length > 0 ? this.bodyStyles.selectedOptions[0] : 'ALL',
      programCode: this.programCodes.selectedOptions.length > 0 ? this.programCodes.selectedOptions[0] : 'ALL',
      startYear: this.startYear,
      endYear: this.endYear,
      srcScenarioId: sourceScenarioId,
      updateComments: false,
    };
  }


  //Filter stuff down here
  /**
   * Function used to update the filtered data based off of the selected filters for a particular filter type
   * @param filterType the filter type (which filter it is)
   * @param selectedFilters list of filter values that were selected from the filter
   */
  applyFilter(filterType: string, selectedFilters: any): void {
    if (selectedFilters.length > 0) {
      this.filteredData = this.filteredData.filter(detailRecord => selectedFilters.includes(detailRecord[filterType].toUpperCase()));
    }
  }

  /**
   * Event handler whenever a filter changes
   * @param filterType the filter type (which filter it is)
   */
  onFilterChange(filterType: string): void {
    // Reset the filtered data to the original data
    this.filteredData = this.allFilterOptions;
    // Reset the selected filters for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
    // Update the filtered data based off of which filters are selected
    if (!this.subregions.selectedOptions.includes('All')) {
      this.applyFilter(this.SUBREGION, this.subregions.selectedOptions);
    }
    if (!this.planningMarkets.selectedOptions.includes('All')) {
      this.applyFilter(this.PLNGMKT, this.planningMarkets.selectedOptions);
    }
    if (!this.marketDivisions.selectedOptions.includes('All')) {
      this.applyFilter(this.MKTDIV, this.marketDivisions.selectedOptions);
    }
    if (!this.brandfams.selectedOptions.includes('All')) {
      this.applyFilter(this.BRANDFAM, this.brandfams.selectedOptions);
    }
    if (!this.bodyStyles.selectedOptions.includes('All')) {
      this.applyFilter(this.BODYSTYLE, this.bodyStyles.selectedOptions);
    }
    if (!this.programCodes.selectedOptions.includes('All')) {
      this.applyFilter(this.PRGMCODE, this.programCodes.selectedOptions);
    }

    // Update the filter options for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
  }

  /**
   * Initialize the filter options for all the filters
   */
  initializeFilterOptions(): void {
    this.subregions.options = this.setFilterOption(this.SUBREGION)
    this.setCascadingFilterOptions(this.SUBREGION);
  }

  /**
   * Helper function to return back a list of available filter options for the specified filter
   * @param filterFieldName the name of the filter to set the filter options for
   */
  setFilterOption(filterFieldName: string): string[] {
    return [...new Set(this.filteredData
    .sort((a, b) => a[filterFieldName].toUpperCase() > b[filterFieldName].toUpperCase() ? 1 : -1)
    .map(detailRecord => detailRecord[filterFieldName].toUpperCase()))];
  }

  /**
   * Function used to set the filter options for the passed in filter + any of the cascaded filters below it
   * @param filterType the filter type (which filter it is)
   */
  setCascadingFilterOptions(filterType: string): void {
    switch (filterType) {
      case this.SUBREGION:
        this.planningMarkets.options = this.setFilterOption(this.PLNGMKT);
        this.planningMarkets.selectedOptions = [];
      // eslint-disable-next-line
      case this.PLNGMKT:
        this.marketDivisions.options = this.setFilterOption(this.MKTDIV);
        this.marketDivisions.selectedOptions = [];
      // eslint-disable-next-line
      case this.MKTDIV:
        this.brandfams.options = this.setFilterOption(this.BRANDFAM);
        this.brandfams.selectedOptions = [];
      // eslint-disable-next-line
      case this.BRANDFAM:
        this.bodyStyles.options = this.setFilterOption(this.BODYSTYLE);
        this.bodyStyles.selectedOptions = [];
      // eslint-disable-next-line
      case this.BODYSTYLE:
        this.programCodes.options = this.setFilterOption(this.PRGMCODE);
        this.programCodes.selectedOptions = [];
    }
  }

  /**
   * Resets the output filters for the pivot table data
   */
  resetAllFilterOptions(): void {
    this.subregions.options = [];
    this.marketDivisions.options = [];
    this.planningMarkets.options = [];
    this.brandfams.options = [];
    this.bodyStyles.options = [];
    this.programCodes.options = [];
  }

  /**
   * Resets all the selected filters from the filter dropdowns
   */
  resetAllSelectedFilter(): void {
    this.subregions.selectedOptions = [];
    this.marketDivisions.selectedOptions = [];
    this.planningMarkets.selectedOptions = [];
    this.brandfams.selectedOptions = [];
    this.bodyStyles.selectedOptions = [];
    this.programCodes.selectedOptions = [];
  }

}
